import _ from 'lodash';
import { Breadcrumb } from '../../../../common/widgets/Breadcrumbs';
import { TopLevelCategories } from '../../enums';
import { Category } from '../../models/Categories';
import { BreadcrumbTrail } from '../../models/Common';
import { TaxFilter } from '../../models/Filters';
import { ProductListModel } from '../../models/Product';
import { resources } from '../../resources/resources';
import { updateUrlParams } from '../CategoryPage/utils';
import { queryParamKeys } from './constants';
import { ICategoryMenuItem, ICategorySideMenuItem, ITaxItem } from './types';

export const mapToMenuItems = (
  translate: any,
  categories?: Category[],
): ICategoryMenuItem[] | undefined => {
  const mapChildren = (
    parentId: number,
    children: Category[],
  ): ICategoryMenuItem[] => {
    if (children.length > 10) {
      const list = _.take(children, 9).map(c => ({
        id: c.redirectCategoryId ? c.redirectCategoryId : c.id,
        title: c.name,
        redirectType: c.redirectType,
        redirectCategoryId: c.redirectCategoryId,
      }));

      return _.concat(
        [
          {
            id: parentId,
            title: translate(resources.seeAll),
          } as ICategoryMenuItem,
        ],
        list,
      );
    }
    return children.map(c => ({
      id: c.redirectCategoryId ? c.redirectCategoryId : c.id,
      title: c.name,
      isSaveEveryday: c.isSaveEveryday,
      redirectType: c.redirectType,
      redirectCategoryId: c.redirectCategoryId,
    }));
  };

  return _.map(categories, (item: Category) => ({
    id: item.redirectCategoryId ? item.redirectCategoryId : item.id,
    title: item.name,
    isSaveEveryday: item.isSaveEveryday,
    showSubmenu: item.showSubmenu,
    taxes: item.taxes,
    redirectType: item.redirectType,
    redirectCategoryId: item.redirectCategoryId,
    children: mapChildren(item.id, item.children),
  }));
};

export const mapToSideMenuItems = (
  categories?: Category[],
  selectedCategoryId?: number,
): ICategorySideMenuItem[] | undefined => {
  const result = _.map(categories, (item: Category) => {
    return {
      id: item.id,
      isSelected:
        item.id === selectedCategoryId ||
        item.children.some(it => it.id === selectedCategoryId),
      title: item.name,
      redirectType: item.redirectType,
      redirectCategoryId: item.redirectCategoryId,
      isSaveEveryday: item.isSaveEveryday,
      children: item.children.map((c: Category) => {
        return {
          id: c.id,
          isSelected: c.id === selectedCategoryId,
          title: c.name,
          redirectType: c.redirectType,
          redirectCategoryId: c.redirectCategoryId,
          isSaveEveryday: c.isSaveEveryday,
        };
      }),
    } as ICategorySideMenuItem;
  });
  return result;
};

export const mapTaxFiltersToTaxItem = (
  taxItems: TaxFilter[],
  checkedtaxItemsIds: number[],
): ITaxItem[] => {
  return _.map(taxItems, t => {
    return {
      taxClassId: t.taxClassId,
      taxRate: _.sumBy(
        _.filter(taxItems, ti => ti.taxClassId === t.taxClassId),
        i => i.rate,
      ),
      isChecked: checkedtaxItemsIds.includes(t.taxClassId),
    };
  });
};

const getTopLevelCategoryBreadcrumb = (
  topLevelCategoryId: number,
  crumbs: BreadcrumbTrail,
): Breadcrumb => {
  switch (topLevelCategoryId) {
    case TopLevelCategories.FlexibleBenefits:
      return crumbs.flexibleBenefits;
    case TopLevelCategories.BenefitClub:
      return crumbs.clubBenefits;
    case TopLevelCategories.DiscountVouchers:
      return crumbs.discountVouchers;
    default:
      return crumbs.cashback;
  }
};

const getTopLevelCategoryPath = (topLevelCategoryId: number): string => {
  switch (topLevelCategoryId) {
    case TopLevelCategories.FlexibleBenefits:
      return 'flexible-benefits';
    case TopLevelCategories.BenefitClub:
      return 'club-benefits';
    case TopLevelCategories.DiscountVouchers:
      return 'discount-vouchers';
    default:
      return 'cashback';
  }
};

const createCategoryBreadcrumb = (
  name: string,
  topLevelCategory: string,
  id: number,
): Breadcrumb => ({
  element: <span>{name}</span>,
  path: `/${topLevelCategory}/${id}`,
});

const createProductBreadcrumb = (
  product: ProductListModel,
  topLevelCategory: string,
  categoryId: number,
): Breadcrumb => ({
  element: <span>{product.brandName}</span>,
  path: `/${topLevelCategory}/${categoryId}?brand=${product.brandId}`,
});

const addDirectCategoryBreadcrumbs = (
  breadcrumbs: Breadcrumb[],
  category: Category,
  topLevelCategory: string,
  product?: ProductListModel,
): void => {
  breadcrumbs.push(
    createCategoryBreadcrumb(category.name, topLevelCategory, category.id),
  );

  if (product) {
    breadcrumbs.push(
      createProductBreadcrumb(product, topLevelCategory, category.id),
    );
  }
};

const addChildCategoryBreadcrumbs = (
  breadcrumbs: Breadcrumb[],
  parentCategory: Category,
  childCategory: Category,
  topLevelCategory: string,
  product?: ProductListModel,
): void => {
  breadcrumbs.push(
    createCategoryBreadcrumb(
      parentCategory.name,
      topLevelCategory,
      parentCategory.id,
    ),
    createCategoryBreadcrumb(
      childCategory.name,
      topLevelCategory,
      childCategory.id,
    ),
  );

  if (product) {
    breadcrumbs.push(
      createProductBreadcrumb(product, topLevelCategory, parentCategory.id),
    );
  }
};

export const getCategoryBreadcrumbs = (
  crumbs: BreadcrumbTrail,
  topLevelCategoryId: number,
  rootCategory: Category | undefined,
  categoryId: string | undefined,
  product?: ProductListModel,
): Breadcrumb[] => {
  const breadcrumbs = [
    crumbs.home,
    getTopLevelCategoryBreadcrumb(topLevelCategoryId, crumbs),
  ];

  if (!rootCategory || !categoryId) {
    return breadcrumbs;
  }

  const topLevelCategory = getTopLevelCategoryPath(topLevelCategoryId);
  const numericCategoryId = Number(categoryId);

  for (const category of rootCategory.children) {
    // Check if this is the target category
    if (category.id === numericCategoryId) {
      addDirectCategoryBreadcrumbs(
        breadcrumbs,
        category,
        topLevelCategory,
        product,
      );
      return breadcrumbs;
    }

    // Check if target is a child of this category
    const childCategory = category.children.find(
      c => c.id === numericCategoryId,
    );
    if (childCategory) {
      addChildCategoryBreadcrumbs(
        breadcrumbs,
        category,
        childCategory,
        topLevelCategory,
        product,
      );
      return breadcrumbs;
    }
  }

  return breadcrumbs;
};

export const clearFilterQueryParams = () => {
  const params = new URLSearchParams(location.search);

  params.delete(queryParamKeys.taxClassIds);

  updateUrlParams(params);
};

export const getInitialTaxClassIdsFromQueryParams = (
  searchParams: URLSearchParams,
): number[] => {
  const taxClassIds: number[] = searchParams
    .getAll(queryParamKeys.taxClassIds)
    .map(Number);

  return taxClassIds;
};

//Verify if a link is internal or not
export const isInternalLink = (link: string): boolean => {
  if (!link) return false;

  if (link.startsWith('/')) return true;

  if (link.startsWith(globalThis.location.origin)) return true;

  if (
    !link.startsWith('http://') &&
    !link.startsWith('https://') &&
    !link.startsWith('//')
  ) {
    return true;
  }

  return false;
};

export const parseRowErrorLines = (rowErrorsString: string): string => {
  // Parse "line, eid, message/line, eid, message/" format
  const errorEntries = rowErrorsString.split('/').filter(entry => entry.trim());
  const lines = errorEntries
    .map(entry => {
      const parts = entry.split(',');
      return parts[0]?.trim();
    })
    .filter(Boolean);

  // Remove duplicates and join
  const uniqueLines = new Set(lines);
  return Array.from(uniqueLines).join(', ');
};
